/* eslint-disable react/prop-types */

import React, { useMemo, useState, useRef, useEffect } from "react";
import { GoogleMap } from "@react-google-maps/api";
import useGoogleMaps from "~/provider/useGoogleMaps";

// FIX PNG IMPORT
const plantsIconUrl = new URL("~/assets/images/growing.png", import.meta.url).href;

const containerStyle = {
  width: "100%",
  height: "500px",
};

// SVG ICON
const createSvgIcon = (color) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <circle cx="24" cy="24" r="22" fill="${color}" />
    <text x="24" y="30" font-size="16" text-anchor="middle" fill="white">🌱</text>
  </svg>
`;

const svgUrl = (svg) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

// NATIVE GOOGLE MARKER
function createMarker(position, iconUrl, map, size = 40) {
  return new window.google.maps.Marker({
    position,
    map,
    icon: {
      url: iconUrl,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2),
    },
  });
}

export default function GoogleMapsNew({ coordinatesArray }) {
  const { isLoaded } = useGoogleMaps();

  const markersRef = useRef([]);
  const infoRef = useRef(null);

  const [mapInstance, setMapInstance] = useState(null);

  const [plotVisible, setPlotVisible] = useState(false);
  const [plantationVisible, setPlantationVisible] = useState({});
  const [detailVisible, setDetailVisible] = useState({});

  const center = useMemo(() => {
    if (!coordinatesArray?.length) return { lat: 0, lng: 0 };
    const last = coordinatesArray[coordinatesArray.length - 1];
    return { lat: Number(last.Latitude), lng: Number(last.Longitude) };
  }, [coordinatesArray]);

  // INFO HTML
  const buildInfoHTML = (node) => {
    const title = node.BarCode_Serial_No || node.Location || "-";
    const total =
      node.plantationdtl?.length ||
      node.plantTationCount ||
      "-";

    return `
      <div style="max-width:240px;">
        <h3>${title}</h3>
        <p><b>Lat:</b> ${node.Latitude}</p>
        <p><b>Lng:</b> ${node.Longitude}</p>
        <p><b>Total:</b> ${total}</p>
      </div>
    `;
  };

  // SHOW & HIDE INFOWINDOW
  const showInfo = (marker, html) => {
    if (!infoRef.current) return;

    infoRef.current.setContent(html);
    infoRef.current.open({
      anchor: marker,
      map: mapInstance,
      pixelOffset: new window.google.maps.Size(0, -40),
    });

    setTimeout(() => {
      const iw = document.querySelector(".gm-style-iw");
      if (iw) iw.classList.add("gm-info-visible");
    }, 10);
  };

  const hideInfo = () => {
    if (infoRef.current) infoRef.current.close();
  };

  const clearMarkers = () => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  };

  // MAIN RENDER LOGIC
  useEffect(() => {
    if (!isLoaded || !mapInstance) return;

    clearMarkers();

    const google = window.google;
    if (!infoRef.current) {
      infoRef.current = new google.maps.InfoWindow();
    }

    const last = coordinatesArray[coordinatesArray.length - 1];
    const mainPos = { lat: Number(last.Latitude), lng: Number(last.Longitude) };

    // MAIN MARKER
    const mainMarker = createMarker(
      mainPos,
      svgUrl(createSvgIcon(last.plantColor || last.plant_mstr?.Plant_Color || "green")),
      mapInstance,
      50
    );

    mainMarker.addListener("mouseover", () =>
      showInfo(mainMarker, buildInfoHTML(last))
    );
    mainMarker.addListener("mouseout", hideInfo);

    mainMarker.addListener("click", () => {
      setPlotVisible((v) => !v);
      showInfo(mainMarker, buildInfoHTML(last));
    });

    markersRef.current.push(mainMarker);

    // LEVEL 1 — PLOTS
    coordinatesArray.slice(0, -1).forEach((plot, plotIdx) => {
      if (!plotVisible) return;

      const pos = {
        lat: Number(plot.Latitude),
        lng: Number(plot.Longitude),
      };

      const plotMarker = createMarker(
        pos,
        svgUrl(createSvgIcon(plot.plantColor || "green")),
        mapInstance,
        42
      );

      plotMarker.addListener("mouseover", () =>
        showInfo(plotMarker, buildInfoHTML(plot))
      );
      plotMarker.addListener("mouseout", hideInfo);

      plotMarker.addListener("click", () => {
        setPlantationVisible((p) => ({ ...p, [plotIdx]: !p[plotIdx] }));
        showInfo(plotMarker, buildInfoHTML(plot));
      });

      markersRef.current.push(plotMarker);

      // LEVEL 2 — PLANTATION LEVEL
      if (plantationVisible[plotIdx] && plot.plantationLocations) {
        plot.plantationLocations.forEach((plant, plantIdx) => {
          const pos2 = {
            lat: Number(plant.Latitude),
            lng: Number(plant.Longitude),
          };

          const key = `${plotIdx}-${plantIdx}`;

          const plantMarker = createMarker(
            pos2,
            svgUrl(createSvgIcon(plant.plant_mstr?.Plant_Color || "green")),
            mapInstance,
            34
          );

          plantMarker.addListener("mouseover", () =>
            showInfo(plantMarker, buildInfoHTML(plant))
          );
          plantMarker.addListener("mouseout", hideInfo);

          plantMarker.addListener("click", () => {
            setDetailVisible((d) => ({ ...d, [key]: !d[key] }));
            showInfo(plantMarker, buildInfoHTML(plant));
          });

          markersRef.current.push(plantMarker);

          // LEVEL 3 — DETAILED PLANT MARKERS
          if (detailVisible[key] && plant.plantationdtl) {
            plant.plantationdtl.forEach((detail) => {
              if (!detail.Latitude || !detail.Longitude) return;

              const pos3 = {
                lat: Number(detail.Latitude),
                lng: Number(detail.Longitude),
              };

              const detailMarker = createMarker(
                pos3,
                plantsIconUrl,
                mapInstance,
                55
              );

              detailMarker.addListener("mouseover", () =>
                showInfo(detailMarker, buildInfoHTML(detail))
              );
              detailMarker.addListener("mouseout", hideInfo);

              detailMarker.addListener("click", () =>
                showInfo(detailMarker, buildInfoHTML(detail))
              );

              markersRef.current.push(detailMarker);
            });
          }
        });
      }
    });
  }, [
    isLoaded,
    mapInstance,
    coordinatesArray,
    plotVisible,
    plantationVisible,
    detailVisible,
  ]);

  return isLoaded ? (
    <GoogleMap
      center={center}
      zoom={16}
      mapContainerStyle={containerStyle}
      mapTypeId="hybrid"
      onLoad={(map) => setMapInstance(map)}
      onUnmount={() => setMapInstance(null)}
    />
  ) : null;
}
